import React, { useEffect, useMemo, useState, useRef } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import { FaBell, FaCheck, FaEllipsisV, FaTrashAlt, FaRegEdit } from "react-icons/fa";
import { IApplicationState } from "../../redux/reducers";
import adminService from "../../services/adminService";
import companyService from "../../services/companyService";
import { IUser } from "../../interfaces/user";
import { USER_ROLES } from "../../interfaces/roles";
import { COMPANY_PERMISSIONS } from "../../interfaces/permissions";
import notification from "../../utils/notification";
import {
	Modal,
	ModalHeader,
	ModalBody,
	ModalFooter,
	Form,
	FormGroup,
	Label,
	Row,
	Col,
	Input
} from "reactstrap";
import Select from "react-select";
import PermissionToggle from "./components/permissionToggle";
import "../../assets/scss/pages/admin/_userRequests.scss";
import { ICompany } from "../../interfaces/company";

import paymentService from "../../services/paymentService";
interface IProps {
	getPendingRequests: (resolve?: Function, reject?: Function) => Promise<any>;
	pendingss: IUserExtended[];
	modifyUser: (userId: string, data: any) => Promise<any>;
}

type AccessStatus = "Active" | "Inactive" | "Suspended";
type SubscriptionType = "Basic" | "Pro" | "Enterprise";

interface IUserExtended extends IUser {
	accessStatus?: AccessStatus;
	subscriptionType?: SubscriptionType;
	trialLevel?: string;
	dateAdded?: string;
	numberOfUsers?: number;
}

const filterTypes = [
	{ key: "Subscription", label: "Subscription" },
	{ key: "Access", label: "Access" },
	{ key: "Trial Level", label: "Trial Level" }
] as const;
type FilterKey = typeof filterTypes[number]["key"];

interface FormData {
	companyName: string;
	companyType: string;
	firstName: string;
	lastName: string;
	email: string;
	phoneNumber: string;
	jobTitle: string;
	city: string;
	state: string;
	country: string;
	website: string;
	subscriptionType: SubscriptionType | string;
	role: USER_ROLES;
}

const UserRequest: React.FC<IProps> = (props) => {
	const [search, setSearch] = useState("");
	const [currentPage, setCurrentPage] = useState(1);

	// filter controls
	const [filterMenuOpen, setFilterMenuOpen] = useState(false);
	const [activeFilter, setActiveFilter] = useState<FilterKey>("Subscription");
	const [filterValueOpen, setFilterValueOpen] = useState(false);
	const [filterValue, setFilterValue] = useState<string>("All");

	const [openActionIdx, setOpenActionIdx] = useState<number | null>(null);
	const itemsPerPage = 50;

	const filterRef = useRef<HTMLDivElement>(null);
	const valueRef = useRef<HTMLDivElement>(null);

	const [modal, setModal] = useState<boolean>(false);

	const [company, setCompany] = useState<ICompany | null>(null);
	const [user, setUser] = useState<IUser>();
	const [updating, setUpdating] = useState<boolean>(false);
	const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

	// ✅ New: dedicated list loading + hydration flags for initial & refresh loads
	const [listLoading, setListLoading] = useState<boolean>(true);
	const [hydrated, setHydrated] = useState<boolean>(false);

	const dispatch = useDispatch();
	const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
	const [sortMenuOpen, setSortMenuOpen] = useState(false);

	const [credits, setCredits] = useState<number>(1250); // initial value, replace with company actual credits
	const [manualCredit, setManualCredit] = useState<number | "">("");
	const [updatingCredits, setUpdatingCredits] = useState(false);
	const [companyTypeOptions, setCompanyTypeOptions] = useState<any[]>([]);
	// 🔑 get current user's role from Redux
	const role = useSelector(
		(state: IApplicationState) =>
			(state as any)?.user?.PROFILE_RESPONSE?.role || (state as any)?.user?.role
	);
	const canDelete = role === USER_ROLES.COMPANY_ADMIN; // only company admin can delete

	const [formData, setFormData] = useState<FormData>({
		companyName: "",
		companyType: "",
		firstName: "",
		lastName: "",
		email: "",
		phoneNumber: "",
		jobTitle: "",
		city: "",
		state: "",
		country: "",
		website: "",
		subscriptionType: "Basic",
		role: USER_ROLES.JF_STAFF
	});

	const handleAddCredits = (amount: number) => {
		setUpdatingCredits(true);
		try {
			// Replace this with real API call if needed
			setCredits((prev) => prev + amount);
			notification.info({ title: "Success", description: `${amount} credits added ✅` });
		} catch (err) {
			notification.error({ title: "Error", description: "Failed to add credits" });
		} finally {
			setUpdatingCredits(false);
		}
	};

	const handleManualCreditAdd = () => {
		const amount = Number(manualCredit);
		if (!amount || amount <= 0) return;
		handleAddCredits(amount);
		setManualCredit("");
	};

	// ✅ Fetch wrapper that updates listLoading/hydrated (avoid flicker)
	const refreshPendingRequests = async () => {
		setListLoading(true);
		try {
			await props.getPendingRequests();
			setHydrated(true);
		} finally {
			setListLoading(false);
		}
	};

	// Prefill right panel with first user when data loads
	useEffect(() => {
		if (props.pendingss && props.pendingss.length > 0 && !user) {
			const firstUser = props.pendingss[0];
			setUser(firstUser);
			setSelectedUserId(firstUser.id);
			setFormData({
				companyName: firstUser.company?.name || "",
				companyType: firstUser.company_type || "",
				firstName: firstUser.first_name || "",
				lastName: firstUser.last_name || "",
				email: firstUser.email || "",
				phoneNumber: firstUser.phone_number || "",
				jobTitle: firstUser.job_title || "",
				city: firstUser.city || "",
				state: firstUser.state || "",
				country: firstUser.country || "",
				website: firstUser.company?.website || "",
				subscriptionType: firstUser.company?.subscription_type || "Basic",
				role: firstUser.role
			});
		}
	}, [props.pendingss, user]);

	// Close menus when clicking outside
	useEffect(() => {
		function handleClick(e: MouseEvent) {
			if (filterMenuOpen && filterRef.current && !filterRef.current.contains(e.target as Node)) {
				setFilterMenuOpen(false);
			}
			if (filterValueOpen && valueRef.current && !valueRef.current.contains(e.target as Node)) {
				setFilterValueOpen(false);
			}
			if (openActionIdx !== null && !(e.target as HTMLElement)?.closest(".actions-dropdown")) {
				setOpenActionIdx(null);
			}
		}
		document.addEventListener("mousedown", handleClick);
		return () => document.removeEventListener("mousedown", handleClick);
	}, [filterMenuOpen, filterValueOpen, openActionIdx]);

	// ✅ Initial fetch (use our wrapper so listLoading is true)
	useEffect(() => {
		refreshPendingRequests();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Fetch company types for dropdown
	useEffect(() => {
		const fetchCompanyTypes = async () => {
			try {
				const types = await companyService.getCompanyTypes()(dispatch);
				setCompanyTypeOptions(types || []);
			} catch (error) {
				console.error("Error fetching company types:", error);
			}
		};
		fetchCompanyTypes();
	}, [dispatch]);

	// Get sorted company type options with current user's type first
	const getSortedCompanyTypeOptions = () => {
		if (!companyTypeOptions.length) return [];

		const currentUserType = user?.company_type;
		const options = companyTypeOptions.map((type: any) => ({
			value: type.title,
			label: type.title
		}));

		if (currentUserType) {
			// Find current user's type and move it to the front
			const currentTypeIndex = options.findIndex(option => option.value === currentUserType);
			if (currentTypeIndex > -1) {
				const currentType = options.splice(currentTypeIndex, 1)[0];
				options.unshift(currentType);
			}
		}

		return options;
	};

	// Build distinct values for current filter
	const distinctValues = useMemo(() => {
		const set = new Set<string>();
		(props.pendingss || []).forEach((u) => {
			let val: string | undefined;
			if (activeFilter === "Access") val = (u.accessStatus || "Inactive") as string;
			else if (activeFilter === "Subscription") val = (u.subscriptionType || "Basic") as string;
			else if (activeFilter === "Trial Level") val = u.trialLevel || "Not on trial";
			if (val) set.add(val);
		});
		return ["All", ...Array.from(set)];
	}, [props.pendingss, activeFilter]);

	// Search + Filter + Sort
	const processedUsers = useMemo(() => {
		let result = [...(props.pendingss || [])];

		if (search.trim()) {
			const q = search.toLowerCase();
			result = result.filter((u) =>
				`${u.company?.name ?? ""} ${u.first_name ?? ""} ${u.last_name ?? ""} ${u.email ?? ""}`
					.toLowerCase()
					.includes(q)
			);
		}

		if (filterValue !== "All") {
			if (activeFilter === "Access") {
				result = result.filter(
					(u) => (u.accessStatus || "Inactive") === (filterValue as AccessStatus)
				);
			} else if (activeFilter === "Subscription") {
				result = result.filter(
					(u) => (u.subscriptionType || "Basic") === (filterValue as SubscriptionType)
				);
			} else if (activeFilter === "Trial Level") {
				result = result.filter((u) => (u.trialLevel || "Not on trial") === filterValue);
			}
		}

		result.sort((a, b) => {
			const dateA = new Date(a.created_at || "").getTime();
			const dateB = new Date(b.created_at || "").getTime();
			return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
		});

		return result;
	}, [props.pendingss, search, activeFilter, filterValue, sortOrder]);

	// Pagination slice
	const paginatedUsers = useMemo(
		() => processedUsers.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage),
		[processedUsers, currentPage]
	);

	useEffect(() => {
		setCurrentPage(1);
	}, [search, activeFilter, filterValue]);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
	};

	const handleCompanyTypeChange = (selectedOption: any) => {
		setFormData((prev) => ({ ...prev, companyType: selectedOption?.value || "" }));
	};

	const selectUser = (u: IUser) => {
		setUser(u);
		setSelectedUserId(u.id);
		setFormData({
			companyName: u.company?.name || "",
			companyType: u.company_type || "",
			firstName: u.first_name || "",
			lastName: u.last_name || "",
			email: u.email || "",
			phoneNumber: u.phone_number || "",
			jobTitle: u.job_title || "",
			city: u.city || "",
			state: u.state || "",
			country: u.country || "",
			website: u.company?.website || "",
			subscriptionType: u.company?.subscription_type || "Basic",
			role: u.role
		});
		setModal(false);
		setOpenActionIdx(null); // Close the dropdown menu

		// Auto-scroll to edit form on small devices
		const isSmallDevice = window.innerWidth <= 1024;
		if (isSmallDevice) {
			// Small delay to ensure the form is rendered
			setTimeout(() => {
				const sideForm = document.querySelector('.user-requests__side-form');
				if (sideForm) {
					sideForm.scrollIntoView({
						behavior: 'smooth',
						block: 'start'
					});
				}
			}, 100);
		}
	};

	const toggleModal = () => setModal(!modal);

	const openSubscriptionModal = () => {
		if (!user?.company) {
			notification.error({
				title: "No Company",
				description: "Selected user has no company."
			});
			return;
		}

		const normalizedCompany: ICompany = {
			...user.company,
			email: user.company.email ?? "",
			subscription_period_end: user.company.subscription_period_end
				? new Date(user.company.subscription_period_end).toISOString()
				: undefined,
			trial_end_date: user.company.trial_end_date
				? new Date(user.company.trial_end_date).toISOString()
				: undefined,
			subscription_type: user.company.subscription_type || ""
		};

		setCompany(normalizedCompany);
		setModal(true);
	};

	const onModifyCompanySubscription = async (option: any) => {
		if (company && option) {
			setUpdating(true);
			try {
				await adminService.updateCompanySubscription(option.value, company.id)(dispatch as any);
			} catch (err) {
				console.error(err);
				notification.error({ title: "Error", description: "Failed to update subscription." });
			} finally {
				setUpdating(false);
			}
		}
	};
	const getPlanLabel = (planType: string): string => {
		switch (planType) {
			case "legacy":
				return "Legacy - $49/month";
			case "supplier":
				return "Supplier - $99/month";
			case "fresh":
				return "Fresh - $149/month";
			case "growth":
				return "Growth - $299/month";
			case "enterprise":
				return "Enterprise - $599/month";
			case "group":
				return "Group - $999/month";
			case "guava":
				return "Guava - $1299/month";
			default:
				return "Select plan";
		}
	};

	const onModifyCompany = async (company: ICompany, permission: { [key: string]: string }) => {
		if (!company) return;
		setUpdating(true);
		const updated_permission = { ...permission };
		try {
			await companyService.updateCompanyPermissions(company, updated_permission)(dispatch as any);
			setCompany((prev) => (prev ? { ...prev, ...updated_permission } : prev));
		} catch (err) {
			console.error(err);
			notification.error({ title: "Error", description: "Failed to update company permissions." });
		} finally {
			setUpdating(false);
		}
	};

	const handlePermissionChange = async (
		company: ICompany,
		permission: COMPANY_PERMISSIONS,
		action: string
	) => {
		const updated_permission = { [permission.toLowerCase()]: action };
		setUpdating(true);
		try {
			await companyService.updateCompanyPermissions(company, updated_permission)(dispatch as any);
			setCompany((prev) => (prev ? { ...prev, ...updated_permission } : prev));
		} catch (err) {
			console.error(err);
			notification.error({
				title: "Error",
				description: "Failed to update company permissions."
			});
		} finally {
			setUpdating(false);
		}
	};

	function companyHasPermission(company: ICompany, permission: COMPANY_PERMISSIONS) {
		const formatted_permission = permission.toLowerCase();
		return companyService.companyHasPermission(company, formatted_permission);
	}

	// ✅ Use the same list fetch wrapper during refresh so the table shows "Loading..." instead of "No users"
	const onModifyUser = async (usr: IUser, data: Partial<IUser> & { company_name?: string }) => {
		setUpdating(true);
		try {
			const payload: any = {};
			if (typeof data.first_name === "string" && data.first_name !== usr.first_name)
				payload.first_name = data.first_name;
			if (typeof data.last_name === "string" && data.last_name !== usr.last_name)
				payload.last_name = data.last_name;
			if (typeof data.email === "string" && data.email !== usr.email) payload.email = data.email;
			if (typeof data.phone_number === "string" && data.phone_number !== usr.phone_number)
				payload.phone_number = data.phone_number;
			if (typeof data.job_title === "string" && data.job_title !== usr.job_title)
				payload.job_title = data.job_title;
			if (typeof data.city === "string" && data.city !== usr.city)
				payload.city = data.city;
			if (typeof data.state === "string" && data.state !== usr.state)
				payload.state = data.state;
			if (typeof data.country === "string" && data.country !== usr.country)
				payload.country = data.country;
			if (typeof data.role === "string" && data.role !== usr.role) payload.role = data.role;
			if (typeof data.is_active === "boolean" && data.is_active !== usr.is_active)
				payload.is_active = data.is_active;
			if (typeof data.disable === "boolean" && data.disable !== usr.disable)
				payload.disable = data.disable;
			if (typeof data.company_type === "string" && data.company_type !== usr.company_type)
				payload.company_type = data.company_type;

			// Handle company_name as a top-level field
			if (typeof data.company_name === "string" && data.company_name !== (usr.company?.name || "")) {
				payload.company_name = data.company_name;
			}

			if (Object.keys(payload).length === 0) return;

			await props.modifyUser(usr.id, payload);

			// optimistic local patch
			setUser((prev) => (prev ? { ...prev, ...data } : prev));

			// ✅ refresh list with proper loading flag
			await refreshPendingRequests();
		} finally {
			setUpdating(false);
		}
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!user) return;
		try {
			const updateData: any = {
				first_name: formData.firstName,
				last_name: formData.lastName,
				email: formData.email,
				phone_number: formData.phoneNumber,
				job_title: formData.jobTitle,
				city: formData.city,
				state: formData.state,
				country: formData.country,
				role: formData.role,
				company_type: formData.companyType
			};

			// Include company_name as a top-level field
			if (formData.companyName !== undefined) {
				const currentName = user.company?.name || "";
				if (formData.companyName !== currentName) {
					updateData.company_name = formData.companyName;
				}
			}

			await onModifyUser(user, updateData);
			notification.info({ title: "Success", description: "User details updated successfully ✅" });
		} catch (err) {
			const anyErr = err as any;
			console.error("Modify user failed:", anyErr?.response?.data || anyErr?.message || anyErr);
			notification.error({
				title: "Update failed",
				description:
					anyErr?.response?.data?.message ||
					anyErr?.message ||
					"Server error while updating the user."
			});
		}
	};

	return (
		<div className="user-requests">
			{/* Main Table Section */}
			<div className="user-requests__table-section">
				{/* Top */}
				<div className="user-requests__top">
					<input
						type="text"
						placeholder="Search customers..."
						value={search}
						onChange={(e) => {
							setSearch(e.target.value);
							setCurrentPage(1);
						}}
						className="user-requests__search"
					/>

					{/* Filter TYPE */}
					<div ref={filterRef} className="user-requests__filter-type">
						<button
							type="button"
							className="user-requests__filter-btn"
							onClick={() => setFilterMenuOpen((v) => !v)}
							aria-haspopup="listbox"
							aria-expanded={filterMenuOpen}
						>
							Filter: {activeFilter} <span>▼</span>
						</button>
						{filterMenuOpen && (
							<div role="listbox" className="user-requests__filter-menu">
								<div className="user-requests__filter-title">Filter by</div>
								{filterTypes.map((filter) => (
									<div
										key={filter.key}
										onClick={() => {
											setActiveFilter(filter.key);
											setFilterValue("All");
											setFilterMenuOpen(false);
										}}
										className={`user-requests__filter-option${activeFilter === filter.key ? " active" : ""
											}`}
										role="option"
										aria-selected={activeFilter === filter.key}
									>
										{activeFilter === filter.key && (
											<FaCheck className="user-requests__filter-check" />
										)}
										{filter.label}
									</div>
								))}
							</div>
						)}
					</div>

					<div className="user-requests__filter-type">
						<button
							type="button"
							className="user-requests__filter-btn"
							onClick={() => setSortMenuOpen((v) => !v)}
							aria-haspopup="listbox"
							aria-expanded={sortMenuOpen}
						>
							Sort by Date: {sortOrder === "asc" ? "Ascending" : "Descending"} <span>▼</span>
						</button>

						{sortMenuOpen && (
							<div role="listbox" className="user-requests__filter-menu">
								<div
									onClick={() => {
										setSortOrder("asc");
										setSortMenuOpen(false);
									}}
									className={`user-requests__filter-option${sortOrder === "asc" ? " active" : ""}`}
									role="option"
									aria-selected={sortOrder === "asc"}
								>
									{sortOrder === "asc" && <FaCheck className="user-requests__filter-check" />}
									Ascending (Oldest → Newest)
								</div>
								<div
									onClick={() => {
										setSortOrder("desc");
										setSortMenuOpen(false);
									}}
									className={`user-requests__filter-option${sortOrder === "desc" ? " active" : ""}`}
									role="option"
									aria-selected={sortOrder === "desc"}
								>
									{sortOrder === "desc" && <FaCheck className="user-requests__filter-check" />}
									Descending (Newest → Oldest)
								</div>
							</div>
						)}
					</div>

					{/* Filter VALUE */}
					<div ref={valueRef} className="user-requests__filter-value">
						<button
							type="button"
							className="user-requests__filter-btn"
							onClick={() => setFilterValueOpen((v) => !v)}
							aria-haspopup="listbox"
							aria-expanded={filterValueOpen}
						>
							Value: {filterValue} <span>▼</span>
						</button>
						{filterValueOpen && (
							<div role="listbox" className="user-requests__filter-menu">
								{distinctValues.map((val) => (
									<div
										key={val}
										onClick={() => {
											setFilterValue(val);
											setFilterValueOpen(false);
										}}
										className={`user-requests__filter-option${filterValue === val ? " active" : ""
											}`}
										role="option"
										aria-selected={filterValue === val}
									>
										{filterValue === val && <FaCheck className="user-requests__filter-check" />}
										{val}
									</div>
								))}
							</div>
						)}
					</div>

					{/* Bell */}
					<div className="user-requests__bell">
						<FaBell />
						<span className="user-requests__notif-dot" />
					</div>
				</div>

				{/* Header + Table */}
				<div className="user-requests__table-container">
					<div className="user-requests__heading">
						<div>
							<h2>Customers</h2>
							<span>Manage your customers and their company details.</span>
						</div>
					</div>

					<div className="user-requests__table-wrapper">
						<div className="table-scroll-container">
							<table className="user-requests__table">
								<thead>
									<tr>
										<th>Full Name</th>
										<th>Company</th>
										<th>Email</th>
										<th>Country</th>
										<th>Role</th>
										<th>Registered Date</th>
										<th>Approval</th>
										<th>Status</th>
										<th></th>
									</tr>
								</thead>
								<tbody>
									{/* ✅ Gate rendering with listLoading/hydrated to prevent the flicker */}
									{listLoading && !hydrated ? (
										<tr>
											<td colSpan={9} className="user-requests__no-users">
												Loading...
											</td>
										</tr>
									) : paginatedUsers.length ? (
										paginatedUsers.map((row, idx) => {
											const accessKey = (row.accessStatus as AccessStatus) || "Inactive";
											const subKey = (row.subscriptionType as SubscriptionType) || "Basic";
											return (
												<tr key={row.id} className={`${idx % 2 ? "odd" : "even"} ${selectedUserId === row.id ? "selected" : ""}`}>
													<td>{`${row.first_name} ${row.last_name}`}</td>
													<td>{row.company?.name || row.company_type || "N/A"}</td>
													<td>{row.email}</td>
													<td>{row.country || "N/A"}</td>
													<td>{row.role}</td>
													{/* <td>{row.subscriptionType}</td> */}
													<td>
														{row.created_at
															? new Date(row.created_at).toLocaleDateString("en-US", {
																year: "numeric",
																month: "short",
																day: "2-digit"
															})
															: "N/A"}
													</td>
													<td>{row.is_active ? "active" : "pending"}</td>
													<td>{row.disable ? "disable" : "enable"}</td>
													<td>
														<div className="actions-wrapper">
															<span
																className="user-requests__ellipsis"
																onClick={() => setOpenActionIdx(openActionIdx === idx ? null : idx)}
															>
																<FaEllipsisV />
															</span>
															{openActionIdx === idx && (
																<div className="actions-dropdown">
																	<div className="actions-dropdown__title">Actions</div>
																	<button type="button" onClick={() => selectUser(row)}>
																		<FaRegEdit /> Edit
																	</button>
																	{canDelete && (
																		<>
																			<div className="actions-dropdown__divider" />
																			<button
																				type="button"
																				className="actions-dropdown__delete"
																				onClick={() => {
																					if (
																						window.confirm(
																							`Delete ${row.first_name} ${row.last_name}?`
																						)
																					) {
																						(dispatch as any)(adminService.removeUser(row.id));
																					}
																				}}
																			>
																				<FaTrashAlt /> Delete
																			</button>
																		</>
																	)}
																</div>
															)}
														</div>
													</td>
												</tr>
											);
										})
									) : (
										<tr>
											<td colSpan={9} className="user-requests__no-users">
												No matching users found
											</td>
										</tr>
									)}
								</tbody>
							</table>
						</div>
					</div>

					{/* Pagination */}
					<div className="user-requests__pagination">
						{listLoading && !hydrated ? (
							<span>Loading…</span>
						) : (
							<span>
								Showing {(currentPage - 1) * itemsPerPage + 1}-
								{Math.min(currentPage * itemsPerPage, processedUsers.length)} of{" "}
								{processedUsers.length} customers
							</span>
						)}
						<div>
							<button
								disabled={listLoading || currentPage === 1}
								onClick={() => setCurrentPage(currentPage - 1)}
								type="button"
							>
								‹ Previous
							</button>
							<button
								disabled={listLoading || currentPage * itemsPerPage >= processedUsers.length}
								onClick={() => setCurrentPage(currentPage + 1)}
								type="button"
							>
								Next ›
							</button>
						</div>
					</div>
				</div>
			</div>

			{/* Side Form Section (unchanged) */}
			<div className="user-requests__side-form">
				<div className="user-requests__side-header">
					<h2>Edit Customer</h2>
					<span>Update details for {formData.companyName || "selected user"}.</span>
					{user && (
						<div className="user-requests__selected-indicator">
							<span className="user-requests__selected-badge">
								✓ Editing: {user.first_name} {user.last_name}
							</span>
						</div>
					)}
				</div>
				<form onSubmit={handleSubmit} className="user-requests__form">
					{user && (
						<div className="user-requests__approval-section">
							<div>Approval</div>
							<div>
								<button
									type="button"
									className={!user?.is_active ? "pending-active" : ""}
									onClick={() => onModifyUser(user, { is_active: false })}
								>
									Pending
								</button>
								<button
									type="button"
									className={user?.is_active ? "active-enabled" : ""}
									onClick={() => onModifyUser(user, { is_active: true })}
								>
									Active
								</button>
							</div>
							<div>Status</div>
							<div>
								<button
									type="button"
									className={user?.disable ? "disabled" : ""}
									onClick={() => onModifyUser(user, { disable: true })}
								>
									Disabled
								</button>
								<button
									type="button"
									className={!user?.disable ? "enabled" : ""}
									onClick={() => onModifyUser(user, { disable: false })}
								>
									Enabled
								</button>
							</div>

							{user?.company && (
								<div className="user-requests__form-field">
									<button type="button" onClick={openSubscriptionModal}>
										Edit Subscription & Features
									</button>
								</div>
							)}
						</div>
					)}

					<div className="user-requests__form-field">
						<label>Company Name</label>
						<input
							type="text"
							name="companyName"
							value={formData.companyName}
							onChange={handleChange}
						/>
					</div>

					<div className="user-requests__form-field">
						<label>Company Type</label>
						<Select
							value={getSortedCompanyTypeOptions().find(option => option.value === formData.companyType)}
							onChange={handleCompanyTypeChange}
							options={getSortedCompanyTypeOptions()}
							placeholder="Select Company Type"
							isClearable
							styles={{
								control: (provided) => ({
									...provided,
									border: "1px solid #cedbe9",
									borderRadius: "4px",
									minHeight: "38px"
								}),
								menu: (provided) => ({
									...provided,
									zIndex: 9999
								})
							}}
						/>
					</div>

					{[
						{ label: "First Name", name: "firstName", type: "text" },
						{ label: "Last Name", name: "lastName", type: "text" },
						{ label: "Email", name: "email", type: "email" },
						{ label: "Phone Number", name: "phoneNumber", type: "text" },
						{ label: "Job Title", name: "jobTitle", type: "text" }
					].map((field) => (
						<div key={field.name} className="user-requests__form-field">
							<label>{field.label}</label>
							<input
								type={field.type}
								name={field.name}
								value={(formData as any)[field.name]}
								onChange={handleChange}
							/>
						</div>
					))}

					<div className="user-requests__form-field">
						<label>Role</label>
						<select name="role" value={formData.role} onChange={handleChange}>
							{Object.values(USER_ROLES).map((r) => (
								<option key={r} value={r}>
									{r}
								</option>
							))}
						</select>
					</div>

					<button type="submit" className="user-requests__save-btn" disabled={updating}>
						{updating ? "Saving..." : "Save Changes"}
					</button>
				</form>
			</div>
			{/* 
      {company && (
        <Modal isOpen={modal} toggle={toggleModal}>
          <ModalHeader toggle={toggleModal}>{company.name}</ModalHeader>
          <ModalBody>
            <Form>
              <FormGroup className="mb-3">
                <Label>Email</Label>
                <Input value={user?.email} placeholder="N/A" disabled={true} style={{ width: "100%", padding: "8px" }} />
              </FormGroup>

              <Row className="mb-3">
                <Col md={12}>
                  <Label>Subscription</Label>
                  <Select
                    defaultValue={{
                      label: company.subscription_type || "None",
                      value: company.subscription_type || ""
                    }}
                    onChange={onModifyCompanySubscription}
                    options={[
                      { label: "Legacy", value: "legacy" },
                      { label: "Supplier", value: "supplier" },
                      { label: "Fresh", value: "fresh" },
                      { label: "Growth", value: "growth" },
                      { label: "Enterprise", value: "enterprise" },
                      { label: "Group", value: "group" },
                      { label: "Guava", value: "guava" },
                      { label: "None", value: "" }
                    ]}
                    styles={{ menu: (provided) => ({ ...provided, zIndex: 9999 }) }}
                  />
                </Col>
              </Row>

              <Row className="mb-3">
                {Object.keys(COMPANY_PERMISSIONS).map((key) => (
                  <Col md={6} key={key}>
                    <PermissionToggle
                      company={company}
                      permission={COMPANY_PERMISSIONS[key as COMPANY_PERMISSIONS]}
                      handlePermissionChange={handlePermissionChange}
                      companyHasPermission={companyHasPermission}
                    />
                  </Col>
                ))}
              </Row>
            </Form>
          </ModalBody>
          <ModalFooter>
            <button className="btn btn-secondary" onClick={toggleModal}>
              Close
            </button>
          </ModalFooter>
        </Modal>
      )} */}
			{company && (
				<Modal isOpen={modal} toggle={toggleModal} className="company-feature-modal">
					<ModalHeader toggle={toggleModal}>{company.name}</ModalHeader>
					<ModalBody>
						<div className="company-feature-section">
							{/* Email Row */}
							<div className="feature-row">
								<span className="feature-label">Email</span>
								<span className="feature-value">{user?.email || "N/A"}</span>
							</div>

							{/* Subscription Dropdown Row */}
							{/* <div className="feature-row">
          <span className="feature-label">Subscription</span>
          <span className="feature-dropdown">
            <Select
              defaultValue={{
                label: company.subscription_type || "None",
                value: company.subscription_type || ""
              }}
              onChange={onModifyCompanySubscription}
              options={[
                { label: "Legacy $111", value: "legacy" },
                { label: "Supplier", value: "supplier" },
                { label: "Fresh", value: "fresh" },
                { label: "Growth", value: "growth" },
                { label: "Enterprise", value: "enterprise" },
                { label: "Group", value: "group" },
                { label: "Guava", value: "guava" },
                { label: "None", value: "" }
              ]}
              styles={{ menu: (provided) => ({ ...provided, zIndex: 9999 }) }}
            />
          </span>
        </div> */}
							<div className="feature-row">
								<span className="feature-label">Subscription</span>
								<span className="feature-dropdown">
									<Select
										defaultValue={{
											label: getPlanLabel(company.subscription_type), // helper to show price
											value: company.subscription_type || ""
										}}
										onChange={onModifyCompanySubscription}
										options={[
											{ label: "Legacy - $49/month", value: "legacy" },
											{ label: "Supplier - $99/month", value: "supplier" },
											{ label: "Fresh - $149/month", value: "fresh" },
											{ label: "Growth - $299/month", value: "growth" },
											{ label: "Enterprise - $599/month", value: "enterprise" },
											{ label: "Group - $999/month", value: "group" },
											{ label: "Guava - $1299/month", value: "guava" }
										]}
										styles={{ menu: (provided) => ({ ...provided, zIndex: 9999 }) }}
									/>
								</span>
							</div>

							{/* Permissions as Add-on Cards */}
							<div className="feature-permission-section">
								<div className="feature-permission-title">Company Features & Permissions</div>
								<div className="addon-card-grid">
									{Object.keys(COMPANY_PERMISSIONS).map((key) => {
										// Update these for your data/pricing/description
										const perm = COMPANY_PERMISSIONS[key as COMPANY_PERMISSIONS];
										const isEnabled = companyHasPermission(company, perm);
										const price = company.pricing?.[perm] || "$99/mo"; // dummy, replace with your price lookup
										const featureDesc = perm.replace(/_/g, " ") + " description"; // dummy description

										return (
											<div className="addon-card" key={key}>
												<div className="addon-card__top">
													<div className="addon-card__title">{perm.replace(/_/g, " ")}</div>
													<div className="addon-card__price">{price}</div>
												</div>
												<div className="addon-card__desc">{featureDesc}</div>
												<div className="addon-card__bottom">
													<label className="addon-card__toggle">
														<input
															type="checkbox"
															checked={isEnabled}
															onChange={() =>
																handlePermissionChange(
																	company,
																	perm,
																	isEnabled ? "disable" : "enable"
																)
															}
														/>
														<span className="addon-card__slider" />
													</label>
													{isEnabled && <span className="addon-card__active">Active</span>}
												</div>
											</div>
										);
									})}
								</div>
							</div>
						</div>
					</ModalBody>
					<ModalFooter>
						<button className="btn btn-secondary" onClick={toggleModal}>
							Close
						</button>
					</ModalFooter>
				</Modal>
			)}
		</div>
	);
};

const mapStateToProps = (state: IApplicationState) => ({
	pendingss: state.admin.pending as IUserExtended[]
});

const mapDispatchToProps = {
	getPendingRequests: (resolve?: Function, reject?: Function) =>
		adminService.getPendingUsers(resolve, reject),
	modifyUser: (userId: string, data: any) => adminService.modifyUser(userId, data)
};

export default connect(mapStateToProps, mapDispatchToProps)(UserRequest);
